import { useTranslation } from 'react-i18next';
import type { PantryRow } from '../../../types';
import { Package, Plus, Icon } from '../../ui/Icon';

interface Props {
  rows: PantryRow[];
  readOnly: boolean;
  onAddMissing: (row: PantryRow) => void;
}

/**
 * The stock of every counted product. It paints what the server computed and
 * formatted (ADR-0014) — no arithmetic here, so mobile shows the same numbers.
 * Absent until a meal names an ingredient: counting is opt-in.
 */
export default function PantryPanel({ rows, readOnly, onAddMissing }: Props) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  // What needs doing first: a product that runs short outranks one that is fine.
  const sorted = [...rows].sort((a, b) => Number(b.missing > 0) - Number(a.missing > 0));

  return (
    <section className="card p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="shrink-0 w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
          <Icon as={Package} size={15} className="text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="flex-1 min-w-0 text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
          {t('meals.pantry.title')}
        </h2>
      </div>
      <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed -mt-1">{t('meals.pantry.hint')}</p>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800 -my-1">
        {sorted.map(row => (
          <li key={row.id} className="py-2.5 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{row.name}</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                {[
                  row.bought > 0 && t('meals.pantry.bought', { amount: row.labels.bought }),
                  row.planned > 0 && t('meals.pantry.planned', { amount: row.labels.planned }),
                  row.pending > 0 && t('meals.pantry.pending', { amount: row.labels.pending }),
                ].filter(Boolean).join(' · ')}
              </p>
            </div>
            {row.missing > 0 ? (
              <div className="shrink-0 flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums bg-rose-50 text-rose-700 dark:bg-rose-900/25 dark:text-rose-300">
                  {t('meals.pantry.missing', { amount: row.labels.missing })}
                </span>
                {!readOnly && (
                  <button
                    onClick={() => onAddMissing(row)}
                    aria-label={t('meals.pantry.addMissingFor', { name: row.name })}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:opacity-90 cursor-pointer"
                  >
                    <Icon as={Plus} size={11} />
                    {t('meals.pantry.addShort')}
                  </button>
                )}
              </div>
            ) : row.bought > 0 ? (
              <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                {t('meals.pantry.left', { amount: row.labels.remainingNow })}
              </span>
            ) : (
              <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {t('meals.pantry.onList')}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
